/**
 * @fileoverview The instructor's Students tab inside a program (I7).
 *
 * Every student in the classes this instructor teaches here, class by class,
 * with the two things they need before the lesson: whether the student can
 * be recorded (consent), and what the organization wrote about them — the
 * instrument they were assigned, its serial, a short operational note
 * (rental, access need, allergy). Read-only: the note is the org's to write,
 * and a two-way field turns into an argument about who is right.
 *
 * Same look as the org portal's Students tab, curated for the instructor: a
 * class filter, consent trailing, the annotation as one line under the name.
 * A class in this program the instructor does not teach is not here at all —
 * the server answers only for the classes the ledger puts them on.
 */

import { useEffect, useMemo, useState } from "react";
import api, { getErrorMessage } from "@/lib/api";
import ExportMenu, { CSV_TYPE, XLSX_TYPE, exportSlug } from "@/components/studio/ExportMenu";
import { Card, ListRow, SectionHeader, SegmentedControl, Tag } from "@/components/studio/primitives";
import { annotationLine } from "@/components/org/RosterAnnotationFields";
import { PROGRAM_ROSTER_LABEL, showsProgramRosterConsent } from "@/lib/consent";

const CONSENT_LABELS = { granted: "Consent", requested: "Asked", declined: "Declined", revoked: "Withdrawn", none: "No consent" };

/* A program roster's student without consent: the organization collects it,
   so the tag names where they came from, in the cool accent, rather than a
   "No consent" that reads as the instructor's to chase. */
const PROGRAM_ROSTER_TAG = "border-[var(--accent-cool-dim)] bg-[var(--accent-cool-dim)] text-[var(--accent-cool)]";

function consentTag(m) {
  if (showsProgramRosterConsent(m)) {
    return (
      <Tag className={PROGRAM_ROSTER_TAG} data-testid={`program-roster-tag-${m.student_id}`}>
        {PROGRAM_ROSTER_LABEL}
      </Tag>
    );
  }
  return <Tag accent={m.consent_status === "granted"}>{CONSENT_LABELS[m.consent_status] || m.consent_status}</Tag>;
}

export default function ProgramStudentsTab({ programId, programTitle, classFilter, onClassFilter }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setFailed(false);
    api
      .get(`/marketplace/memberships/${programId}/roster`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [programId]);

  const classes = useMemo(() => data?.classes || [], [data]);
  const options = useMemo(
    () => [
      { id: "all", label: "All classes" },
      ...classes.map((c) => ({ id: c.class_id, label: c.name, count: c.members.length })),
    ],
    [classes],
  );
  const shown = classFilter && classFilter !== "all" ? classes.filter((c) => c.class_id === classFilter) : classes;
  const total = classes.reduce((n, c) => n + c.members.length, 0);

  if (failed) {
    return (
      <Card data-testid="program-students-failed">
        <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Couldn&rsquo;t load the students in this program.</p>
      </Card>
    );
  }
  if (!data) return <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</p>;

  return (
    <div data-testid="program-students">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <SectionHeader className="mb-0">Students ({total})</SectionHeader>
        {/* The classes this instructor teaches here, never the rest of the
            program: the server answers from the same ledger this tab does. */}
        {classes.length > 0 && (
          <ExportMenu
            client={api}
            errorMessage={getErrorMessage}
            testId="export-program-students"
            items={[
              {
                id: "roster",
                label: "Roster",
                hint: "CSV · your classes in this program",
                url: `/marketplace/memberships/${programId}/roster/export`,
                filename: `hans-${exportSlug(programTitle, "program")}-roster.csv`,
                type: CSV_TYPE,
              },
              {
                id: "attendance",
                label: "Attendance",
                hint: "Spreadsheet · a tab for each class",
                url: `/marketplace/memberships/${programId}/attendance/export`,
                filename: `hans-${exportSlug(programTitle, "program")}-attendance.xlsx`,
                type: XLSX_TYPE,
              },
            ]}
          />
        )}
      </div>
      {data.annotation_help && (
        <p className="mb-3 max-w-2xl font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="program-students-help">
          {data.annotation_help}
        </p>
      )}
      {classes.length > 1 && (
        <div className="-mx-4 mb-4 overflow-x-auto px-4 md:mx-0 md:px-0">
          <SegmentedControl
            testId="program-students-filter"
            className="whitespace-nowrap"
            value={classFilter || "all"}
            onChange={(id) => onClassFilter?.(id === "all" ? null : id)}
            options={options}
          />
        </div>
      )}
      {classes.length === 0 ? (
        <Card data-testid="program-students-empty">
          <p className="font-sans text-[15px] text-[var(--text-tertiary)]">
            You don&rsquo;t teach a class in this program yet, so there is nobody to list.
          </p>
        </Card>
      ) : (
        shown.map((cls) => (
          <div key={cls.class_id} className="mb-6" data-testid={`program-students-class-${cls.class_id}`}>
            {(shown.length > 1 || classes.length > 1) && (
              <p className="mb-2 font-sans text-[13px] font-medium text-[var(--text-secondary)]">
                {cls.name} · {cls.members.length} student{cls.members.length === 1 ? "" : "s"}
              </p>
            )}
            <StudentRosterCard members={cls.members} />
          </div>
        ))
      )}
    </div>
  );
}

/**
 * One class's students, as the program's Students tab lists them: name, the
 * org's annotation as a line underneath, and consent (and Archived) trailing.
 * Shared with the class page's Students tab so a class reads the same wherever
 * it is opened.
 */
export function StudentRosterCard({ members }) {
  return (
    <Card padding={0}>
      {members.length === 0 ? (
        <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">Nobody is enrolled yet.</div>
      ) : (
        members.map((m, i) => (
          <ListRow
            key={m.student_id}
            borderBottom={i < members.length - 1}
            title={m.name}
            wrapText
            subtitle={annotationLine(m.annotation) || undefined}
            trailing={
              <span className="flex items-center gap-2">
                {m.archived && <Tag>Archived</Tag>}
                {consentTag(m)}
              </span>
            }
            data-testid={`program-student-${m.student_id}`}
          />
        ))
      )}
    </Card>
  );
}
